/**
 * /campaigns/:id/variants — A/B/C/N variantes de templates HSM por campanha
 * (Story 11.4 backend + 11.4b frontend, Epic E11).
 *
 * Surface:
 *   - Header: voltar pra detail + nome da campanha
 *   - Botão "Adicionar variante" → abre VariantFormModal em modo create
 *   - Lista tabular ordenada por position ASC + variant_letter ASC
 *   - Row actions: Edit (abre modal em mode edit) / Remover (soft delete c/ confirm)
 *   - Estados: loading skeleton, empty CTA, error retry, mutation pending
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BarChart3, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, apiClient } from '@/lib/api';
import {
  VariantFormModal,
  type CampaignTemplateDto,
} from '@/components/campaigns/VariantFormModal';
import { ABTestingToggle } from '@/components/campaigns/ABTestingToggle';

interface CampaignLite {
  id: string;
  name: string;
}

interface ListResponse {
  campaign_id: string;
  variants: CampaignTemplateDto[];
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function CampaignVariantsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<CampaignTemplateDto | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<CampaignTemplateDto | null>(null);

  const campaignQuery = useQuery({
    queryKey: ['admin', 'campaigns', id],
    queryFn: () => apiClient.get<CampaignLite>(`/admin/campaigns/${id!}`),
    enabled: !!id,
  });

  const listQuery = useQuery({
    queryKey: ['admin', 'campaigns', id, 'templates'],
    queryFn: () => apiClient.get<ListResponse>(`/admin/campaigns/${id!}/templates`),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: (variantId: string) =>
      apiClient.delete<{ deleted: boolean; soft: boolean }>(
        `/admin/campaigns/${id!}/templates/${variantId}`,
      ),
    onSuccess: () => {
      toast.success('Variante desativada (soft delete).');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'campaigns', id, 'templates'],
      });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao remover variante.'));
    },
  });

  if (!id) return null;

  function openCreate() {
    setEditingVariant(undefined);
    setModalOpen(true);
  }

  function openEdit(variant: CampaignTemplateDto) {
    setEditingVariant(variant);
    setModalOpen(true);
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/campaigns/${id}`)}>
          <ArrowLeft aria-hidden className="mr-1" /> Voltar
        </Button>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Variantes de template{campaignQuery.data ? ` — ${campaignQuery.data.name}` : ''}
            </h1>
            <p className="text-sm text-muted-foreground">
              A/B/C/N testing dos templates Meta usados nessa campanha. Atribuição round-robin
              determinística por <code>position ASC, letter ASC</code>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/campaigns/${id}/metrics`)}
              data-testid="metrics-btn"
            >
              <BarChart3 className="mr-1 h-4 w-4" aria-hidden />
              Métricas
            </Button>
            <Button onClick={openCreate} data-testid="add-variant-btn">
              <Plus className="mr-1 h-4 w-4" aria-hidden />
              Adicionar variante
            </Button>
          </div>
        </div>
      </div>

      {listQuery.data && (
        <ABTestingToggle
          campaignId={id}
          activeCount={listQuery.data.variants.filter((v) => v.active).length}
          totalCount={listQuery.data.variants.length}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Variantes configuradas</CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <div data-testid="variants-loading" className="space-y-2">
              <div className="h-8 animate-pulse rounded bg-muted" />
              <div className="h-8 animate-pulse rounded bg-muted" />
              <div className="h-8 animate-pulse rounded bg-muted" />
            </div>
          ) : listQuery.error ? (
            <div
              role="alert"
              data-testid="variants-error"
              className="space-y-2 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            >
              <div>{humanizeError(listQuery.error, 'Falha ao carregar variantes.')}</div>
              <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : !listQuery.data || listQuery.data.variants.length === 0 ? (
            <div
              data-testid="variants-empty"
              className="space-y-3 rounded-md border border-dashed bg-muted/30 px-3 py-8 text-center"
            >
              <p className="text-sm text-muted-foreground">
                Nenhuma variante configurada — adicione a primeira pra começar o A/B.
              </p>
              <Button onClick={openCreate}>
                <Plus className="mr-1 h-4 w-4" aria-hidden />
                Adicionar primeira variante
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-sm" role="table" aria-label="Variantes da campanha">
                <thead className="border-b bg-muted/40 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Letra
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Template Meta
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Posição
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Mappings
                    </th>
                    <th scope="col" className="px-3 py-2 font-medium">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data.variants.map((v) => (
                    <tr
                      key={v.id}
                      className="border-b last:border-b-0"
                      data-testid={`variant-row-${v.variant_letter}`}
                    >
                      <td className="px-3 py-2 align-middle">
                        <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold uppercase text-primary">
                          {v.variant_letter}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle font-mono text-xs">
                        <Link
                          to={`/templates`}
                          className="text-primary hover:underline"
                          title={v.meta_template_id}
                        >
                          {v.meta_template_id.slice(0, 8)}…
                        </Link>
                      </td>
                      <td className="px-3 py-2 align-middle">{v.position}</td>
                      <td className="px-3 py-2 align-middle text-xs text-muted-foreground">
                        {Object.keys(v.variable_mappings ?? {}).length === 0
                          ? '—'
                          : `${Object.keys(v.variable_mappings).length} slot(s)`}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <span
                          className={
                            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ' +
                            (v.active
                              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                              : 'bg-muted text-muted-foreground')
                          }
                        >
                          {v.active ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right align-middle">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(v)}
                          aria-label={`Editar variante ${v.variant_letter}`}
                        >
                          <Pencil className="h-4 w-4" aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(v)}
                          aria-label={`Remover variante ${v.variant_letter}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <VariantFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        campaignId={id}
        variant={editingVariant}
      />

      <Dialog open={deleteTarget !== null} onOpenChange={(next) => !next && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remover variante {deleteTarget?.variant_letter}?</DialogTitle>
            <DialogDescription>
              Soft delete — conversas em andamento NÃO são afetadas. Novos disparos param de usar
              esta variante imediatamente. Você pode reativar editando de novo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              disabled={deleteMutation.isPending}
              data-testid="confirm-delete-btn"
            >
              {deleteMutation.isPending ? 'Removendo…' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CampaignVariantsPage;
